import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Table, TableRow, TableBody, TableCell,
  Typography, TableContainer,
  TablePagination, TableHead, Stack,
  InputAdornment,
  TextField,
  IconButton,
  Card,
  Box
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import axios from 'axios';
import { API_URL, FILE_UPLOAD_TYPE } from '../constant';
import { includes, isEmpty } from 'lodash';
import { LoginContext } from '../context/logincontext';
import FileStatus from './FileStatus';

export default function DealerMissingRcs() {

  const navigate = useNavigate();

  const context = useContext(LoginContext)
  const dealer = context?.user?.company
  const dealerId = dealer?.dealer_id

  const accessLevel = dealer?.evcred_access || []
  const allowedToUpload = includes(accessLevel, 'RC Upload')

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [value, setValue] = useState([]);
  const [filteredValue, setFilteredValue] = useState([]);
  const [showDealer, setShowDealer] = useState(false);


  useEffect(() => {
    fetchMissingRcData();
  }, [dealerId]);

  const fetchMissingRcData = async () => {
    const response = await axios.get(`${API_URL}/dealers/${dealerId}/missing_rcs_details`);
    if (response.data.status === 'success') {
      const dealerLoans = response.data.data?.reverse() || [];
      const showDealer = !isEmpty(dealerLoans?.[0]?.firm_name)
      setShowDealer(showDealer)
      setValue(dealerLoans);
      setFilteredValue(dealerLoans);
    }
  };

  const redirect = (loanDetails) => {
    const loanId = loanDetails?.loan_id;
    const loanDealerId = loanDetails?.dealer_id || dealerId;
    navigate(`/dealers/${loanDealerId}/loan/${loanId}/details`, { state: { loanDetails } });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - value.length) : 0;
  const isUserNotFound = !filteredValue.length && !isEmpty(value);

  const handleFilter = (e) => {
    const query = e.target.value.toLowerCase();
    const filteredData = value.filter((v) =>
      Object.values(v).some((val) =>
        String(val).toLowerCase().includes(query)
      )
    );
    setFilteredValue(filteredData);
  };

  const uploadFileBody = (v) => {
    return {
      customer_id: v.customer_id,
      loan_id: v.loan_id,
      relation_id: 0,
      doc_name: v?.customer_name
    };
  }

  return (
    <Page>
      <Card sx={{ width: 'auto', p: 3, boxShadow: 3, borderRadius: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}
        >
          <Typography variant="h4" sx={{ fontFamily: 'sans-serif', borderBottom: 1, borderColor: 'secondary.main' }}>
            Pending Rc
          </Typography>
          <TextField
            type="search"
            variant="outlined"
            onChange={handleFilter}
            placeholder="Search/ Loan Id/ Customer/ Loan Type..."
            sx={{ width: 500, backgroundColor: 'white', borderRadius: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Scrollbar>
          <TableContainer sx={{ width: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Loan ID</TableCell>
                  <TableCell align="center">Customer Name</TableCell>
                  <TableCell align="center">Registration No</TableCell>
                  <TableCell align="center">Loan Type</TableCell>
                  {showDealer && <TableCell align="center">Dealer Name</TableCell>}
                  <TableCell align="center">Verification Status</TableCell>
                  <TableCell align="center">HPN Verified</TableCell>
                  {allowedToUpload && <TableCell align="center">Upload Rc</TableCell>}

                </TableRow>
              </TableHead>

              <TableBody>
                {filteredValue.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((v, index) => (
                  <TableRow hover key={index}>
                    <TableCell
                      align="center"
                      style={{ cursor: 'pointer', color: 'blue' }}
                      onClick={() => redirect(v)}
                    >
                      {v?.loan_id}
                    </TableCell>
                    <TableCell align="center">{v?.customer_name}</TableCell>
                    <TableCell align="center">{v?.registration_number || 'N/A'}</TableCell>
                    <TableCell align="center">{v?.type}</TableCell>
                    {showDealer && <TableCell align="center">{v?.firm_name}</TableCell>}
                    <TableCell align="center" style={{ color: v?.verified ? 'green' : 'orange' }}>{v?.verified ? 'verified' : 'pending'}</TableCell>
                    <TableCell align="center" style={{ color: v?.is_hpn_ascend ? 'green' : 'orange' }}>{v?.is_hpn_ascend ? 'Yes' : 'No'}</TableCell>
                    {allowedToUpload && (isEmpty(v.rc_file_path) ? <TableCell align="center">
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        <FileStatus
                          label={FILE_UPLOAD_TYPE.VEHICLE_RC?.toUpperCase().replaceAll('_', ' ')}
                          fileType={FILE_UPLOAD_TYPE.VEHICLE_RC}
                          details={uploadFileBody(v)}
                          onSuccess={fetchMissingRcData}
                        />
                      </div>
                    </TableCell> :
                      <TableCell align="center">
                        <Typography sx={{ color: 'darkgrey' }}>Pending Review</Typography></TableCell>

                    )}

                  </TableRow>
                ))}

                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}

                {isUserNotFound && (
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                      <SearchNotFound searchQuery={''} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 500]}
          component="div"
          count={value.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    </Page>
  );
}
